import { create } from "zustand";

import { Asset, AssetState } from "__graphql__/types";

import { SupplierName } from "../utils/constants";

export type ReportedAsset = {
  id: string;
  state: AssetState;
};

type AssetManagementStore = {
  scannedBarcode: string | null;
  searchedAsset: Asset | null;
  matchedSuppliersFromScan: SupplierName[];
  shouldUpdateAsset: boolean;
  shouldCreateAsset: boolean;
  shouldTransferAsset: boolean;
  currentHubAssets: Asset[];
  countedAssets: Asset[];
  missingAssets: Asset[];
  reportedAssets: ReportedAsset[];
  currentTaskId: string | null;
  countSessionId: string | null;
};

type AssetManagementStoreWithMethods = AssetManagementStore & {
  setScannedBarcode(scannedBarcode: string | null): void;
  setSearchedAsset(searchedAsset: Asset | null): void;
  setMatchedSuppliersFromScan(matchedSuppliersFromScan: SupplierName[]): void;
  setShouldUpdateAsset(shouldUpdateAsset: boolean): void;
  setShouldCreateAsset(shouldCreateAsset: boolean): void;
  setShouldTransferAsset(shouldTransferAsset: boolean): void;
  setCurrentHubAssets(currentHubAssets: Asset[]): void;
  setCountedAssets(countedAssets: Asset[]): void;
  addCountedAsset(asset: Asset): void;
  removeCountedAsset(assetId: string): void;
  setMissingAssets(missingAssets: Asset[]): void;
  setReportedAsset(reportedAsset: ReportedAsset): void;
  removeReportedAsset(assetId: string): void;
  setCurrentTaskId(currentTaskId: string | null): void;
  setCountSessionId(countSessionId: string): void;
  resetMutationFlags(): void;
  reset(): void;
  resetCount(): void;
};

const initialState: AssetManagementStore = {
  scannedBarcode: null,
  searchedAsset: null,
  matchedSuppliersFromScan: [],
  currentHubAssets: [],
  countedAssets: [],
  missingAssets: [],
  reportedAssets: [],
  currentTaskId: null,
  countSessionId: null,
  shouldUpdateAsset: false,
  shouldCreateAsset: false,
  shouldTransferAsset: false,
};

export const useAssetManagementStore = create<AssetManagementStoreWithMethods>()((set) => ({
  ...initialState,
  setScannedBarcode: (scannedBarcode) => set(() => ({ scannedBarcode })),
  setSearchedAsset: (searchedAsset) => set(() => ({ searchedAsset })),
  setMatchedSuppliersFromScan: (matchedSuppliersFromScan) =>
    set(() => ({ matchedSuppliersFromScan })),
  setShouldUpdateAsset: (shouldUpdateAsset) => set(() => ({ shouldUpdateAsset })),
  setShouldCreateAsset: (shouldCreateAsset) => set(() => ({ shouldCreateAsset })),
  setShouldTransferAsset: (shouldTransferAsset) => set(() => ({ shouldTransferAsset })),
  setCurrentHubAssets: (currentHubAssets) => set(() => ({ currentHubAssets })),
  resetCurrentHubAssets: () => set(() => ({ currentHubAssets: [] })),
  setCountedAssets: (countedAssets) => set(() => ({ countedAssets })),
  addCountedAsset: (asset) =>
    set((state) => ({
      countedAssets: [asset, ...state.countedAssets.filter(({ id }) => id !== asset.id)],
    })),
  removeCountedAsset: (assetId) =>
    set((state) => ({ countedAssets: state.countedAssets.filter(({ id }) => id !== assetId) })),
  setMissingAssets: (missingAssets) => set(() => ({ missingAssets })),
  setReportedAsset: (reportedAsset) =>
    set((state) => ({
      reportedAssets: [
        ...state.reportedAssets.filter((asset) => asset.id !== reportedAsset.id),
        reportedAsset,
      ],
    })),
  removeReportedAsset: (assetId) =>
    set((state) => ({
      reportedAssets: state.reportedAssets.filter((asset) => asset.id !== assetId),
    })),
  setCurrentTaskId: (currentTaskId) => set(() => ({ currentTaskId })),
  setCountSessionId: (countSessionId) => set(() => ({ countSessionId })),
  resetCount: () => set(() => ({ countedAssets: [], missingAssets: [], reportedAssets: [] })),
  resetMutationFlags: () =>
    set(() => ({
      shouldUpdateAsset: false,
      shouldCreateAsset: false,
      shouldTransferAsset: false,
    })),
  reset: () => set(() => ({ ...initialState })),
}));
